import React, { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { RoutesPaths } from "../../Router/constant";
import ScoreGauge from "../../Components/Charts/ScoreGauge";
import {
  AnchorIcon,
  ArrowRightIcon,
  BankIcon,
  BuildingIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CreditCardIcon,
  DocCheckIcon,
  EditIcon,
  MoreIcon,
  NoteIcon,
  ReceiptIcon,
  ReportIcon,
  ScaleIcon,
  ShieldCheckIcon,
  TrendUpIcon,
  UserIcon,
  UsersIcon,
} from "../../Components/Icons";
import { DETAIL_TABS, TAXPAYERS, TAXPAYER_DETAIL } from "./data";
import "./Taxpayers.css";

const BREAKDOWN_ICONS = {
  filing: ScaleIcon,
  payments: CreditCardIcon,
  vat: ReceiptIcon,
  withholding: DocCheckIcon,
  documents: DocCheckIcon,
};

const CONNECTION_ICONS = {
  building: BuildingIcon,
  receipt: ReceiptIcon,
  customs: AnchorIcon,
  users: UsersIcon,
  bank: BankIcon,
};

const QUICK_ACTIONS = [
  { label: "Edit Profile", icon: EditIcon },
  { label: "Add Case Note", icon: NoteIcon },
  { label: "Generate Report", icon: ReportIcon },
  { label: "More", icon: MoreIcon },
];

function CardHead({ title, chevron }) {
  return (
    <div className="dgi-card__header">
      <h2 className="dgi-card__title">
        {title}
        {chevron && (
          <span className="dgi-card__info">
            <ChevronRightIcon size={14} />
          </span>
        )}
      </h2>
      <button type="button" className="dgi-card__more" aria-label="More options">
        <MoreIcon size={18} />
      </button>
    </div>
  );
}

export default function TaxpayerDetail() {
  const { nif } = useParams();
  const [activeTab, setActiveTab] = useState("Overview");

  // Only Blue Horizon has a full 360° record in the mock set; other rows fall
  // back to it so every list link opens a populated page.
  const listed = TAXPAYERS.find((item) => item.nif === nif);
  const taxpayer = {
    ...TAXPAYER_DETAIL,
    ...(listed
      ? {
          nif: listed.nif,
          name: listed.name,
          status: listed.status,
          score: listed.score,
          riskLevel: listed.risk,
        }
      : {}),
  };

  return (
    <div className="page">
      <nav className="crumbs" aria-label="Breadcrumb">
        <Link to={RoutesPaths.TAXPAYERS}>Taxpayers</Link>
        <ChevronRightIcon size={14} />
        <span aria-current="page">Taxpayer 360°</span>
      </nav>

      {/* ------------------------------------------------------- profile -- */}
      <div className="profile-row">
        <div>
          <section className="dgi-card profile h-100">
            <span className="profile__avatar">
              <BuildingIcon size={38} />
            </span>

            <div className="profile__body">
              <div className="profile__name-row">
                <h1 className="profile__name">{taxpayer.name}</h1>
                <span
                  className={`chip chip--dot chip--${taxpayer.status.toLowerCase()}`}
                >
                  {taxpayer.status}
                </span>
              </div>

              <dl className="profile__meta">
                {taxpayer.summary.map((item) => (
                  <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.label === "NIF" ? taxpayer.nif : item.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="profile__badges">
                {taxpayer.badges.map((badge) => (
                  <span
                    key={badge.label}
                    className={`chip chip--dot chip--${badge.tone}`}
                  >
                    {badge.label}
                  </span>
                ))}
              </div>
            </div>
          </section>
        </div>

        <div>
          <section className="dgi-card scorecard h-100">
            <p className="scorecard__label">Compliance Score</p>
            <p className="scorecard__value">
              {taxpayer.score}
              <span> / 100</span>
            </p>
            <p className="scorecard__risk">{taxpayer.riskLevel} Risk</p>
            <span className="score__track">
              <span
                className="score__fill score__fill--good"
                style={{ width: `${taxpayer.score}%` }}
              />
            </span>
          </section>
        </div>

        <div>
          <section className="dgi-card h-100">
            <p className="scorecard__label">Quick Actions</p>
            <div className="quick-actions">
              {QUICK_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    type="button"
                    className="quick-action"
                    key={action.label}
                  >
                    <Icon size={19} />
                    <span>{action.label}</span>
                  </button>
                );
              })}
            </div>
          </section>
        </div>
      </div>

      {/* ---------------------------------------------------------- tabs -- */}
      <div className="dgi-card detail-tabs">
        <div className="tabs tabs--scroll" role="tablist">
          {DETAIL_TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={activeTab === tab}
              className={`tabs__tab${
                activeTab === tab ? " tabs__tab--active" : ""
              }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      {activeTab !== "Overview" ? (
        <section className="dgi-card">
          <p className="dgi-table__empty">
            The “{activeTab}” tab is not built yet.
          </p>
        </section>
      ) : (
        <div className="row g-3">
          {/* ------------------------------------------ left column -- */}
          <div className="col-12 col-xl-4">
            <div className="stack">
              <section className="dgi-card">
                <CardHead title="Company Information" chevron />
                <dl className="kv">
                  {taxpayer.company.map((item) => (
                    <div className="kv__row" key={item.label}>
                      <dt>{item.label}</dt>
                      <dd className={item.tone ? `kv__dd--${item.tone}` : ""}>
                        {item.tone && <span className="kv__dot" />}
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="dgi-card">
                <CardHead title="Recent Activity" />
                <ol className="timeline">
                  {taxpayer.activity.map((entry) => (
                    <li className="timeline__item" key={entry.title}>
                      <span
                        className={`timeline__marker${
                          entry.done ? " timeline__marker--done" : ""
                        }`}
                      >
                        {entry.done && <CheckCircleIcon size={14} />}
                      </span>
                      <div>
                        <p className="timeline__title">{entry.title}</p>
                        <p className="timeline__meta">
                          {entry.when} · {entry.by}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            </div>
          </div>

          {/* ---------------------------------------- middle column -- */}
          <div className="col-12 col-xl-5">
            <div className="stack">
              <section className="dgi-card">
                <CardHead title="Compliance Score" chevron />
                <div className="score-panel">
                  <ScoreGauge value={taxpayer.score} />
                  <ul className="breakdown">
                    {taxpayer.scoreBreakdown.map((item) => {
                      const Icon = BREAKDOWN_ICONS[item.icon];
                      return (
                        <li className="breakdown__row" key={item.label}>
                          <Icon size={18} />
                          <span className="breakdown__label">{item.label}</span>
                          <span className="breakdown__value">
                            {item.value} / 100
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                <p className="dgi-card__foot">
                  Last calculated on {taxpayer.scoreCalculatedOn}
                </p>
              </section>

              <section className="dgi-card">
                <CardHead title="Connected Records" />
                <p className="dgi-card__lede">
                  All key records are connected and up to date.
                </p>
                <div className="row g-2">
                  {taxpayer.connections.map((item) => {
                    const Icon = CONNECTION_ICONS[item.icon];
                    return (
                      <div className="col-12 col-sm-6 col-lg-4" key={item.label}>
                        <div className="connection">
                          <Icon size={19} />
                          <div>
                            <p className="connection__label">{item.label}</p>
                            <p className="connection__state">Connected</p>
                            <p className="connection__sync">
                              Last sync: {item.sync}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <button type="button" className="dgi-card__link">
                  View all connections <ArrowRightIcon size={16} />
                </button>
              </section>
            </div>
          </div>

          {/* ----------------------------------------- right column -- */}
          <div className="col-12 col-xl-3">
            <div className="stack">
              <section className="dgi-card">
                <CardHead title="Risk Summary" />
                <div className="risk-summary">
                  <span className="risk-summary__icon">
                    <ShieldCheckIcon size={28} />
                  </span>
                  <div>
                    <p className="risk-summary__level">
                      {taxpayer.riskLevel} Risk
                    </p>
                    <p className="risk-summary__note">
                      No immediate risk detected
                    </p>
                  </div>
                </div>

                <dl className="kv kv--tight">
                  <div className="kv__row">
                    <dt>Risk Score</dt>
                    <dd>{taxpayer.riskScore} / 100</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Risk Level</dt>
                    <dd>{taxpayer.riskLevel}</dd>
                  </div>
                  <div className="kv__row">
                    <dt>Last Risk Review</dt>
                    <dd>{taxpayer.lastRiskReview}</dd>
                  </div>
                </dl>

                <button type="button" className="dgi-btn dgi-btn--outline">
                  <TrendUpIcon size={17} />
                  View Risk Analysis
                </button>
              </section>

              <section className="dgi-card">
                <CardHead title="Key Contacts" />
                <ul className="contacts">
                  {taxpayer.contacts.map((contact) => (
                    <li className="contact" key={contact.role}>
                      <span className="contact__icon">
                        <UserIcon size={18} />
                      </span>
                      <div>
                        <p className="contact__role">{contact.role}</p>
                        <p className="contact__name">{contact.name}</p>
                        <p className="contact__line">{contact.phone}</p>
                        <p className="contact__line">{contact.email}</p>
                      </div>
                    </li>
                  ))}
                </ul>
                <button type="button" className="dgi-card__link">
                  View all contacts <ArrowRightIcon size={16} />
                </button>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
